import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Grid, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { Add, DeleteOutline, Edit, LibraryMusic, MusicNote, QueueMusic } from '@mui/icons-material';
import AppBarContext from 'context/AppBarContext';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import PillTabs from 'components/shared/tabs/PillTabs';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import PlayMusicPlayer from 'components/play/music/PlayMusicPlayer';
import PlayAlbumBuilder from 'components/play/music/PlayAlbumBuilder';
import PlayMusicTrackModal from 'components/play/admin/PlayMusicTrackModal';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import {
  ADMIN_PLAY_MUSIC_ALBUMS,
  ADMIN_PLAY_MUSIC_TRACKS,
  DELETE_PLAY_MUSIC_ALBUM,
  DELETE_PLAY_MUSIC_TRACK
} from 'api/graphql/playMusic';
import { formatDuration } from 'components/play/workoutEstimate';

// Los ids siguen el contrato de `tabsA11yProps` que PillTabs aplica por índice.
function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}>
      {value === index && children}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired
};

/**
 * OTRIX Play (admin/entrenadora) — librería de música relajante para las prácticas de
 * recuperación. Dos pestañas: las PISTAS sueltas (subir, editar, ocultar) y los ÁLBUMES
 * curados por OTRIX, que se arman con el mismo constructor que usa el cliente.
 */
function PlayMusicAdminPage() {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const pageTitle = t('musicAdmin.pageTitle', { ns: 'play', defaultValue: 'Música Play' });

  const [tab, setTab] = useState(0);
  const [editorTrack, setEditorTrack] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [builderAlbum, setBuilderAlbum] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [albumTarget, setAlbumTarget] = useState(null);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const { data, loading } = useQuery(ADMIN_PLAY_MUSIC_TRACKS, { fetchPolicy: 'cache-and-network' });
  const [deleteTrack] = useMutation(DELETE_PLAY_MUSIC_TRACK, {
    refetchQueries: [{ query: ADMIN_PLAY_MUSIC_TRACKS }]
  });

  const { data: albumsData, loading: albumsLoading } = useQuery(ADMIN_PLAY_MUSIC_ALBUMS, {
    fetchPolicy: 'cache-and-network'
  });
  const [deleteAlbum] = useMutation(DELETE_PLAY_MUSIC_ALBUM, {
    refetchQueries: [{ query: ADMIN_PLAY_MUSIC_ALBUMS }]
  });

  const tracks = data?.adminPlayMusicTracks || [];
  const albums = albumsData?.adminPlayMusicAlbums || [];
  const firstLoad = loading && !data;
  const albumsFirstLoad = albumsLoading && !albumsData;
  const isAlbumsTab = tab === 1;

  const notify = (variant, message) =>
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `music-${Date.now()}`, variant, message })));

  const handleDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deleteTrack({ variables: { id: target.id } });
      notify('success', t('musicAdmin.deleted', { ns: 'play', defaultValue: 'Pista eliminada.' }));
    } catch {
      notify('error', t('musicAdmin.deleteError', { ns: 'play', defaultValue: 'No pudimos eliminar la pista.' }));
    }
  };

  const handleDeleteAlbum = async () => {
    const target = albumTarget;
    setAlbumTarget(null);
    try {
      await deleteAlbum({ variables: { id: target.id } });
      notify('success', t('albums.deleted', { ns: 'play', defaultValue: 'Playlist eliminada.' }));
    } catch {
      notify('error', t('albums.deleteError', { ns: 'play', defaultValue: 'No pudimos eliminar la playlist.' }));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <SEOPageTitle title={pageTitle} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack spacing={0.25}>
          <Typography variant="h5" fontWeight={800} letterSpacing="-0.01em">
            {pageTitle}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('musicAdmin.subtitle', { ns: 'play', defaultValue: 'La música que acompaña las prácticas de recuperación de los clientes.' })}
          </Typography>
        </Stack>
        <Button
          startIcon={<Add />}
          onClick={() => {
            if (isAlbumsTab) {
              setBuilderAlbum(null);
              setBuilderOpen(true);
              return;
            }
            setEditorTrack(null);
            setEditorOpen(true);
          }}
          sx={{ ...premiumButtonSx, flex: 'none' }}>
          {isAlbumsTab
            ? t('albums.new', { ns: 'play', defaultValue: 'Nuevo álbum' })
            : t('musicAdmin.new', { ns: 'play', defaultValue: 'Nueva pista' })}
        </Button>
      </Stack>

      <PillTabs
        hasPanels
        value={tab}
        onChange={(_, v) => setTab(v)}
        ariaLabel={t('musicAdmin.tabsLabel', { ns: 'play', defaultValue: 'Secciones de la librería' })}
        sx={{ mb: 2 }}
        tabs={[
          { key: 'tracks', label: t('musicAdmin.tabTracks', { ns: 'play', defaultValue: 'Pistas' }), icon: <MusicNote /> },
          { key: 'albums', label: t('musicAdmin.tabAlbums', { ns: 'play', defaultValue: 'Álbumes' }), icon: <LibraryMusic /> }
        ]}
      />

      <TabPanel value={tab} index={0}>
        {firstLoad && (
          <Grid container spacing={2}>
            {[0, 1, 2].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rounded" height={170} sx={{ borderRadius: '16px' }} />
              </Grid>
            ))}
          </Grid>
        )}

        {!firstLoad && tracks.length === 0 && (
          <PremiumCard>
            <Stack spacing={1.5} alignItems="center" sx={{ py: 4, textAlign: 'center' }}>
              <MusicNote sx={{ fontSize: 44, color: 'text.disabled' }} />
              <Typography variant="h6" fontWeight={800}>
                {t('musicAdmin.emptyTitle', { ns: 'play', defaultValue: 'Aún no hay música' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 380 }}>
                {t('musicAdmin.emptyBody', { ns: 'play', defaultValue: 'Sube pistas relajantes (MP3, M4A, WAV u OGG) y los clientes podrán usarlas en sus prácticas de descanso.' })}
              </Typography>
            </Stack>
          </PremiumCard>
        )}

        {!firstLoad && tracks.length > 0 && (
          <Grid container spacing={2}>
            {tracks.map((track) => (
              <Grid item xs={12} sm={6} md={4} key={track.id}>
                <Stack
                  spacing={1.25}
                  sx={{
                    height: '100%',
                    p: 2,
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'hairline.main',
                    backgroundColor: 'surface.content',
                    boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight)',
                    transition: 'transform 220ms cubic-bezier(0.32,0.72,0,1)',
                    '&:hover': { transform: 'translateY(-2px)' }
                  }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2" fontWeight={800} sx={{ flex: 1, minWidth: 0 }} noWrap>
                      {track.title}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label={t('musicAdmin.edit', { ns: 'play', defaultValue: 'Editar pista' })}
                      onClick={() => {
                        setEditorTrack(track);
                        setEditorOpen(true);
                      }}
                      sx={{ color: 'text.secondary' }}>
                      <Edit sx={{ fontSize: 17 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={t('musicAdmin.delete', { ns: 'play', defaultValue: 'Eliminar pista' })}
                      onClick={() => setDeleteTarget(track)}
                      sx={{ color: 'sessionStatus.absent.fg' }}>
                      <DeleteOutline sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={t(`musicAdmin.categories.${track.category}`, { ns: 'play', defaultValue: track.category })}
                      sx={{ bgcolor: 'gold.soft', color: 'gold.main', fontWeight: 700, height: 20, fontSize: '0.66rem' }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                      {formatDuration(track.duration_seconds)}
                    </Typography>
                    {!track.is_active && (
                      <Chip
                        size="small"
                        label={t('musicAdmin.inactive', { ns: 'play', defaultValue: 'Oculta' })}
                        sx={{ bgcolor: 'surface.overlay', color: 'text.secondary', fontWeight: 700, height: 20, fontSize: '0.66rem' }}
                      />
                    )}
                  </Stack>

                  <Box sx={{ mt: 'auto' }}>
                    <PlayMusicPlayer track={track} />
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tab} index={1}>
        {albumsFirstLoad && (
          <Grid container spacing={2}>
            {[0, 1, 2].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rounded" height={210} sx={{ borderRadius: '16px' }} />
              </Grid>
            ))}
          </Grid>
        )}

        {!albumsFirstLoad && albums.length === 0 && (
          <PremiumCard>
            <Stack spacing={1.5} alignItems="center" sx={{ py: 4, textAlign: 'center' }}>
              <QueueMusic sx={{ fontSize: 44, color: 'text.disabled' }} />
              <Typography variant="h6" fontWeight={800}>
                {t('albums.adminEmptyTitle', { ns: 'play', defaultValue: 'Aún no hay álbumes' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 380 }}>
                {t('albums.adminEmptyBody', {
                  ns: 'play',
                  defaultValue: 'Agrupa las pistas en álbumes (respiración, sueño, enfoque) y los clientes los verán listos para reproducir.'
                })}
              </Typography>
            </Stack>
          </PremiumCard>
        )}

        {!albumsFirstLoad && albums.length > 0 && (
          <Grid container spacing={2}>
            {albums.map((album) => (
              <Grid item xs={12} sm={6} md={4} key={album.id}>
                <Stack
                  spacing={1.25}
                  sx={{
                    height: '100%',
                    p: 2,
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'hairline.main',
                    backgroundColor: 'surface.content',
                    boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight)',
                    transition: 'transform 220ms cubic-bezier(0.32,0.72,0,1)',
                    '&:hover': { transform: 'translateY(-2px)' }
                  }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2" fontWeight={800} sx={{ flex: 1, minWidth: 0 }} noWrap>
                      {album.title}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label={t('albums.edit', { ns: 'play', defaultValue: 'Editar playlist' })}
                      onClick={() => {
                        setBuilderAlbum(album);
                        setBuilderOpen(true);
                      }}
                      sx={{ color: 'text.secondary' }}>
                      <Edit sx={{ fontSize: 17 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={t('albums.delete', { ns: 'play', defaultValue: 'Eliminar playlist' })}
                      onClick={() => setAlbumTarget(album)}
                      sx={{ color: 'sessionStatus.absent.fg' }}>
                      <DeleteOutline sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={t('albums.trackCount', {
                        ns: 'play',
                        count: album.track_count,
                        defaultValue: '{{count}} pistas'
                      })}
                      sx={{ bgcolor: 'gold.soft', color: 'gold.main', fontWeight: 700, height: 20, fontSize: '0.66rem' }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                      {formatDuration(album.total_seconds)}
                    </Typography>
                    {!album.is_active && (
                      <Chip
                        size="small"
                        label={t('musicAdmin.inactive', { ns: 'play', defaultValue: 'Oculta' })}
                        sx={{ bgcolor: 'surface.overlay', color: 'text.secondary', fontWeight: 700, height: 20, fontSize: '0.66rem' }}
                      />
                    )}
                  </Stack>

                  {/* El player ya trae la cola plegable: no hace falta repetir la lista. */}
                  <Box sx={{ mt: 'auto' }}>
                    <PlayMusicPlayer tracks={album.tracks} />
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <PlayMusicTrackModal open={editorOpen} track={editorTrack} onClose={() => setEditorOpen(false)} />

      <PlayAlbumBuilder
        open={builderOpen}
        album={builderAlbum}
        onClose={() => setBuilderOpen(false)}
      />

      <ConfirmDeleteModal
        open={Boolean(albumTarget)}
        onClose={() => setAlbumTarget(null)}
        onConfirm={handleDeleteAlbum}
        title={t('albums.deleteTitle', { ns: 'play', defaultValue: 'Eliminar playlist' })}
        message={t('albums.deleteMessageAdmin', {
          ns: 'play',
          name: albumTarget?.title || '',
          defaultValue: '¿Eliminar el álbum "{{name}}"? Las pistas siguen en la librería, solo se borra la lista.'
        })}
      />

      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('musicAdmin.deleteTitle', { ns: 'play', defaultValue: 'Eliminar pista' })}
        message={t('musicAdmin.deleteMessage', {
          ns: 'play',
          name: deleteTarget?.title || '',
          defaultValue: '¿Eliminar "{{name}}" de la librería? Las prácticas que la usen quedarán sin música.'
        })}
      />
    </Box>
  );
}

export default PlayMusicAdminPage;
